/**
 * Global date-format override.
 *
 * The store operates in South Africa and the user wants dates rendered
 * day-first ("09/05/2026"). Different parts of the codebase call
 * `Date.prototype.toLocaleDateString()` / `toLocaleString()` with a mix of
 * locales (no-arg, "en-US", "en-ZA"…) which produced an inconsistent
 * combination of "5/9/2026", "2026/05/09", and "9/5/2026" depending on
 * where the page was rendered.
 *
 * Rather than refactor ~200 individual call sites, we patch the two Date
 * prototype methods so that any locale that defaults to month-first
 * (en-US) or year-first (en-ZA) is silently remapped to en-GB, which
 * always emits day-first DD/MM/YYYY. Explicit non-English locales are
 * left untouched. Number.prototype.toLocaleString is a separate
 * prototype and is NOT touched, so currency / number formatting is
 * unaffected.
 *
 * Import this module once, at the top of frontend/src/index.js, before
 * any component renders.
 */

const DAY_FIRST_LOCALE = "en-GB";

// Locales we want to override to day-first. Anything else (e.g. "fr",
// "de", explicit "en-IN") is left as the caller intended.
const REMAP_LOCALES = new Set([
  undefined,
  null,
  "",
  "en",
  "en-US",
  "en-us",
  "en-ZA",
  "en-za",
  "default",
]);

const shouldRemap = (locale) => {
  if (Array.isArray(locale)) {
    // `Intl` accepts arrays; remap if the first preference is one we want to override.
    return shouldRemap(locale[0]);
  }
  return REMAP_LOCALES.has(locale);
};

const _origToLocaleDateString = Date.prototype.toLocaleDateString;
const _origToLocaleString = Date.prototype.toLocaleString;

// eslint-disable-next-line no-extend-native
Date.prototype.toLocaleDateString = function patchedToLocaleDateString(locale, options) {
  const effectiveLocale = shouldRemap(locale) ? DAY_FIRST_LOCALE : locale;
  return _origToLocaleDateString.call(this, effectiveLocale, options);
};

// eslint-disable-next-line no-extend-native
Date.prototype.toLocaleString = function patchedToLocaleString(locale, options) {
  const effectiveLocale = shouldRemap(locale) ? DAY_FIRST_LOCALE : locale;
  return _origToLocaleString.call(this, effectiveLocale, options);
};
